import { Link } from 'react-router-dom';
import { X, Mail, Music, Youtube } from 'lucide-react';
import { CleviaLogo } from '@/components/CleviaLogo';
import { useTranslation } from '@/hooks/useTranslation';

interface FooterLink {
  label: string;
  href: string;
}

interface FooterSection {
  title: string;
  links: FooterLink[];
}

const SOCIAL_LINKS = [
  { href: 'https://x.com/Cleviaapp', icon: X, label: 'X' },
  { href: 'https://www.tiktok.com/@clevia.app', icon: Music, label: 'TikTok' },
  { href: 'https://www.youtube.com/@cleviaapp', icon: Youtube, label: 'YouTube' },
  { href: 'mailto:founders@cleviaapp.com', icon: Mail, label: 'Email' },
];

const LINK_CLASS =
  'text-sm text-muted-foreground transition-colors duration-200 hover:text-brand-700 dark:hover:text-brand-400';

export function LandingFooter() {
  const { t } = useTranslation();

  const footerSections: FooterSection[] = [
    {
      title: t('footer.features.title' as any) || 'Features',
      links: [
        { label: t('footer.features.budget' as any) || 'Budget Management', href: '#' },
        { label: t('footer.features.tracking' as any) || 'Transaction Tracking', href: '#' },
        { label: t('footer.features.wealth' as any) || 'Wealth Tracking', href: '#' },
        { label: t('footer.features.goals' as any) || 'Goals', href: '#' },
      ],
    },
    {
      title: t('footer.resources.title' as any) || 'Resources',
      links: [
        { label: t('footer.resources.guide' as any) || 'Getting Started', href: '/help' },
        { label: t('footer.resources.faq' as any) || 'FAQ', href: '#' },
        { label: t('footer.resources.blog' as any) || 'Blog', href: '#' },
        { label: t('footer.resources.help' as any) || 'Help Center', href: '#' },
      ],
    },
    {
      title: t('footer.company.title' as any) || 'Company',
      links: [
        { label: t('footer.company.about' as any) || 'About Us', href: '/founders' },
        { label: t('footer.company.press' as any) || 'Press', href: '/press' },
      ],
    },
    {
      title: t('footer.legal.title' as any) || 'Legal',
      links: [
        { label: t('footer.legal.terms' as any) || 'Terms of Use', href: '/p/terms' },
        { label: t('footer.legal.privacy' as any) || 'Privacy Policy', href: '/p/privacy' },
        { label: t('footer.legal.cookies' as any) || 'Cookies', href: '/p/cookies' },
        { label: t('footer.legal.legal' as any) || 'Legal Notice', href: '/p/legal' },
      ],
    },
  ];

  return (
    <footer className="border-t border-border bg-background">
      <div className="mx-auto max-w-6xl px-6 py-20 lg:py-24">
        {/* Logo et colonnes */}
        <div className="grid grid-cols-2 gap-x-8 gap-y-12 md:grid-cols-3 lg:grid-cols-5">
          <div className="col-span-2 md:col-span-3 lg:col-span-1">
            <CleviaLogo size="md" label="clevia App" className="mb-4" />
            <p className="max-w-xs text-pretty text-sm leading-relaxed text-muted-foreground">
              {t('footer.tagline' as any) || 'Understand your financial situation, without stress.'}
            </p>
          </div>

          {footerSections.map((section, idx) => (
            <div key={idx}>
              <h4 className="mb-5 text-[11px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                {section.title}
              </h4>
              <ul className="space-y-3">
                {section.links.map((link, i) => (
                  <li key={i}>
                    {link.href.startsWith('mailto:') ? (
                      <a href={link.href} className={LINK_CLASS}>
                        {link.label}
                      </a>
                    ) : (
                      <Link to={link.href} className={LINK_CLASS}>
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bas de page */}
        <div className="mt-16 flex flex-col gap-6 border-t border-border pt-8 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground">
            {t('footer.copyright' as any) || '© 2026 clevia. All rights reserved.'}
          </p>

          <div className="flex items-center gap-2">
            {SOCIAL_LINKS.map(({ href, icon: Icon, label }) => (
              <a
                key={label}
                href={href}
                aria-label={label}
                {...(href.startsWith('http')
                  ? { target: '_blank', rel: 'noopener noreferrer' }
                  : {})}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors duration-200 hover:border-primary/40 hover:text-brand-700 dark:hover:text-brand-400"
              >
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
